import React from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import Dialog from 'components/dialog'
import { Grid, MenuItem, Typography } from '@material-ui/core'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import Link from '@material-ui/core/Link'
import SyncAltOutlinedIcon from '@material-ui/icons/SyncAltOutlined'
import { FormProvider, useForm } from 'react-hook-form'
import { useStyles } from './styles'
import { CreateEndorsementInvitation } from './queries'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import { useIsMobile } from 'lib/hooks'
import {
  CtrlTextField,
  CtrlCheckbox,
  EmailFieldWithSuggestions,
} from 'components/form-util'

const validationSchema = yup.object().shape({
  firstName: yup
    .string()
    .min(1)
    .required(),
  lastName: yup
    .string()
    .min(1)
    .required(),
  email: yup
    .string()
    .email()
    .required(),
  title: yup
    .string()
    .min(1)
    .required(),
  facilityId: yup.string().required(),
  agreePolicies: yup
    .boolean()
    .oneOf([true], 'You must agree to continue')
    .required(),
})

const NewSponsorshipModal = ({ trigger, onAdd, facilities }) => {
  const [CreateEndorsement] = useMutation(CreateEndorsementInvitation)

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      title: '',
      facilityId: '',
    },
    resolver: yupResolver(validationSchema),
  })

  const {
    handleSubmit,
    control,
    errors,
    formState: { isSubmitting },
    watch,
  } = form

  const { agreePolicies } = watch()
  const { enqueueSnackbar } = useSnackbar()
  const isMobile = useIsMobile()

  const classes = useStyles()
  const CodeOfEthicsLink =
    'https://resources.pga.org/my-membership/governance/pga-constitution-and-bylaws'

  const onSubmit = closeDialog => async formValues => {
    try {
      const response = await CreateEndorsement({
        variables: {
          input: {
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            email: formValues.email,
            title: formValues.title,
            facilityId: formValues.facilityId,
          },
        },
      })
      if (response.data.createEndorsementInvitation.success) {
        enqueueSnackbar('Endorsement Created', {
          variant: 'success',
        })
        onAdd && onAdd()
        closeDialog()
      }
    } catch (e) {
      enqueueSnackbar('Sorry, an error occurred', {
        variant: 'error',
      })
    }
  }

  return (
    <Dialog
      trigger={trigger}
      scroll="paper"
      fullWidth
      fullScreen={isMobile}
      showCloseButton
    >
      {({ closeDialog }) => {
        return (
          <>
            <DialogTitle>New PGA Coach Sponsorship</DialogTitle>
            <Divider />
            <FormProvider {...form}>
              <form onSubmit={handleSubmit(onSubmit(closeDialog))}>
                <DialogContent className={classes.root}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                      >
                        Complete this form to sponsor non-PGA of America members
                        at your facility to gain access to the PGA Coach
                        platform.
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ marginTop: '20px', fontWeight: 500 }}
                      >
                        Coach Information
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <CtrlTextField
                        name="firstName"
                        label="First Name"
                        required
                        {...{ control, errors }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CtrlTextField
                        name="lastName"
                        label="Last Name"
                        required
                        {...{ control, errors }}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} style={{ marginTop: '12px' }}>
                    <EmailFieldWithSuggestions
                      name="email"
                      label="Email *"
                      variant="outlined"
                      control={control}
                      errors={errors}
                      classes={classes}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" className={classes.subtitle}>
                      What is their job title?
                    </Typography>
                    <CtrlTextField
                      name="title"
                      label="Title"
                      required
                      {...{ control, errors }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" className={classes.subtitle}>
                      What facility are they associated with?
                    </Typography>
                    <CtrlTextField
                      select
                      label="Facility"
                      required
                      name="facilityId"
                      variant="outlined"
                      control={control}
                      errors={errors}
                    >
                      {facilities?.map(facility => (
                        <MenuItem key={facility.id} value={facility.id}>
                          {facility.name}
                        </MenuItem>
                      ))}
                    </CtrlTextField>
                  </Grid>
                  <Grid item xs={12}>
                    <CtrlCheckbox
                      control={control}
                      errors={errors}
                      name="agreePolicies"
                      label={
                        <>
                          By sponsoring a non-member to join the PGA Coach
                          platform, I assert that they work at my facility and
                          are in good standing with the PGA. I understand and
                          agree to abide by the&nbsp;
                          <Link
                            href={CodeOfEthicsLink}
                            target="_blank"
                            underline="always"
                          >
                            Code of Ethics
                          </Link>
                          &nbsp;and PGA Coach User Policies.
                        </>
                      }
                    />
                  </Grid>
                </DialogContent>
                <DialogActions style={{ marginBottom: '32px' }}>
                  <Button onClick={() => closeDialog()}>Cancel</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SyncAltOutlinedIcon />}
                    disabled={isSubmitting || !agreePolicies}
                  >
                    Sponsor
                  </Button>
                </DialogActions>
              </form>
            </FormProvider>
          </>
        )
      }}
    </Dialog>
  )
}

NewSponsorshipModal.propTypes = {
  trigger: PropTypes.node.isRequired,
  onAdd: PropTypes.func,
  facilities: PropTypes.arrayOf(PropTypes.shape({})),
}

export default NewSponsorshipModal
